import { useMemo } from 'react';

import { EModule } from '@lumahealthhq/ui-common';

import useSitePatient from './useSitePatient';

export default function useModulesProviderRoutingMap() {
  const sitePatient = useSitePatient();

  const routingMap = useMemo(
    () => ({
      [EModule.ContainerAdmin]: '/admin',
      [EModule.ContainerAuthLogin]: '/login',
      // TODO rrigonia,diegopessigatt the Header is using the "ContainerAuthLogin" instead of "ContainerAuthStaffLogin" to logout, we should change it and use only the StaffLogin module
      [EModule.ContainerAuthStaffLogin]: '/login',
      [EModule.ContainerAuthPasswordForgot]: '/forgot-password',
      [EModule.ContainerAuthPasswordReset]: '/reset-password/:token',
      [EModule.ContainerAuthSignup]: '/signup',
      [EModule.ContainerAuthSignupDemo]: '/demo-setup',
      [EModule.ContainerCopayDetermination]: 'copay-determination/:id',
      [EModule.ContainerCopayDeterminationAppointmentDetails]: 'copay-determination/details/:id',
      [EModule.ContainerChat]: '/hub',
      [EModule.Hub]: '/hub',
      [EModule.ContainerHome]: '/',
      [EModule.ContainerInsightsCenterReports]: '/insights-center/featured-reports',
      [EModule.ContainerInsightsOverview]: '/insights-center/overview',
      [EModule.ContainerInsightsCenterMonitoring]: '/insights-center/monitoring',
      [EModule.ContainerTableClosedChatRecords]: '/insights-center/monitoring/hub-activity',
      [EModule.ContainerTableSparkMonitoring]: '/insights-center/monitoring/navigator',
      [EModule.ContainerDetailsSparkMonitoring]: '/insights-center/monitoring/navigator/:id',
      [EModule.ContainerInsightsRecommendations]: '/insights-center/recommendations',
      [EModule.ContainerROICalculator]: '/insights-center/executive-summary',
      [EModule.ContainerMessagingActivity]: '/messaging-activity/summary',
      [EModule.ContainerTableNumberManagement]: '/v2/settings/utilities/outbound-numbers',
      [EModule.ContainerFormNumberManagement]: '/v2/settings/utilities/outbound-numbers/:id',
      [EModule.ContainerNotFound]: '/notfound',
      [EModule.ContainerPatient]: '/patients/:id/:section',
      ['ContainerFormThirdPartyIntegrationNumberManagement' as EModule]: '/v2/settings/utilities/third-party-integrations/number-management/:vendor',
      [EModule.ContainerPatientFormTemplateLaunch]: `${sitePatient}/survey`,
      [EModule.ContainerPatientFormTemplates]: '/v2/form-builder',
      [EModule.ContainerPatientInsurance]: '/patients/:id/insurances/:insuranceId',
      [EModule.ContainerPatientSchedule]: '/patients/:id/scheduler',
      [EModule.ContainerProviderUtilization]: '/insights-center/provider-utilization',
      [EModule.ContainerReports]: '/reports/:tab',
      [EModule.ContainerSchedulerCalendar]: '/schedule', // ScheduleV2
      [EModule.ContainerSettingsHome]: '/v2/settings/home',
      [EModule.ContainerStaffScheduler]: '/scheduler-assist',
      [EModule.ContainerTableAppointments]: '/schedule',
      [EModule.ContainerTableApptTypes]: '/v2/settings/practice-resources/appointment-types',
      [EModule.ContainerTableBroadcasts]: '/broadcasts',
      // TODO: @dev-LucasOliveira remove shadowing
      ['ContainerBroadcastFlow' as EModule]: '/broadcasts/:id',
      [EModule.ContainerTableChecklistTemplate]: '/v2/settings/utilities/checklist-builder',
      // TODO: @phelipe-ohlsen remove shadowing
      ['ContainerQueueManagerBuilder' as EModule]: '/v2/settings/utilities/queue-manager-builder/:id',
      ['ContainerTableQueueManagerTemplate' as EModule]: '/v2/settings/utilities/queue-manager-builder',
      [EModule.ContainerTableDocuments]: '/file-management',
      [EModule.ContainerTableFacilities]: '/v2/settings/practice-resources/facilities',
      [EModule.ContainerTableFormResponses]: '/form-responses',
      [EModule.ContainerTableGroups]: '/v2/settings/security-center/groups',
      [EModule.ContainerTablePayments]: '/lumapay/summary',
      [EModule.ContainerTableOffers]: '/offers/:id',
      [EModule.ContainerTablePatientForms]: '/file-management',
      [EModule.ContainerTablePatientJourney]: '/insights-center/patient-journey',
      [EModule.ContainerTablePatients]: '/patients',
      [EModule.ContainerTableProviders]: '/v2/settings/practice-resources/providers',
      [EModule.ContainerTableRecalls]: '/recalls',
      [EModule.ContainerTableFaxAutomation]: '/faxes',
      [EModule.ContainerFaxDetails]: '/faxes/:id',
      [EModule.ContainerTableReferralsInbound]: '/inbound-referrals',
      [EModule.ContainerTableReferralsOutbound]: '/outbound-referrals',
      [EModule.ContainerTableResourceSync]: '/v2/settings/integration-health/:type',
      [EModule.ContainerTableUploadCenter]: '/uploadCenter',
      [EModule.ContainerTableUsers]: '/v2/settings/security-center/users',
      [EModule.ContainerTableWaitlists]: '/waitlists',
      [EModule.ContainerTableFollowupWorkflowInstances]: '/followup-workflows/:followupWorkflow',
      [EModule.ContainerTableFollowupWorkflows]: '/v2/settings/followup-workflows',
      [EModule.ContainerFollowupWorkflowBuilder]: '/v2/settings/followup-workflows/builder',
      [EModule.ContainerWaitingRoom]: '/waiting-room',
      [EModule.FormAppt]: '/patients/:patient/appointments',
      [EModule.FormApptType]: '/v2/settings/practice-resources/appointment-types/:id',
      [EModule.FormBroadcast]: '/broadcasts/:id',
      [EModule.FormChecklistTemplate]: '/v2/settings/utilities/checklist-builder/:id',
      [EModule.ContainerFormFaxWorkflow]: '/v2/settings/spark-management/fax-transform-automations/:id',
      [EModule.ContainerTableFaxDocumentTypeManagement]:
        '/v2/settings/spark-management/fax-transform-automations/document-type-management',
      [EModule.ContainerTableFaxWorkflows]: '/v2/settings/spark-management/fax-transform-automations',
      [EModule.ContainerTableAssistants]: '/v2/settings/spark-management/assistants',
      [EModule.ContainerFormFaxDocumentTypeManagement]:
        '/v2/settings/spark-management/fax-transform-automations/document-type-management/:id',
      [EModule.ContainerBuilderAssistants]: '/v2/settings/spark-management/assistants/:id',
      [EModule.FormFacility]: '/v2/settings/practice-resources/facilities/:id',
      [EModule.FormGroup]: '/v2/settings/security-center/groups/:id',
      [EModule.FormInsurance]: '/patients/:patient/insurances/:id',
      [EModule.FormLumaBotFlow]: '/patients/:patient/lumabot-session/:id',
      [EModule.FormLumaBotFlowTemplate]: '/v2/settings/utilities/lumabot-builder/:id',
      [EModule.FormOffer]: '/patients/:patient/offers',
      [EModule.FormPatient]: '/patients/:id/edit',
      [EModule.FormPatientForm]: '/patients/:patient/formAnswers/:id',
      [EModule.FormPatientFormReviewAnswers]: '/patients/:patient/formAnswers/:id/review',
      [EModule.FormPatientFormTemplate]: '/v2/form-builder/:id',
      [EModule.FormPatientMessageTemplate]: '/v2/settings/utilities/message-library/:type',
      ContainerEligibilitySetup: '/v2/settings/utilities/eligibility-setup',
      [EModule.FormProvider]: '/v2/settings/practice-resources/providers/:id',
      [EModule.FormReferralInbound]: '/inbound-referrals/:id',
      [EModule.FormReferralOutbound]: '/outbound-referrals/:id',
      [EModule.FormReferralUpload]: '/inbound-referrals/upload',
      [EModule.FormReminder]: '/patients/:patient/reminders',
      [EModule.FormUser]: '/v2/settings/security-center/users/:id',
      [EModule.FormWaitlist]: '/waitlists/:id',
      [EModule.FormWorkflow]: '/workflows/:id',
      [EModule.UtilityCustomActions]: '/v2/settings/utilities/custom-actions/appointment/:id',
      [EModule.Telehealth]: '/telehealth',
      [EModule.ContainerTableBroadcastTemplates]: '/v2/settings/utilities/broadcast-templates/:id',
    }),
    [sitePatient],
  );

  return routingMap;
}
